import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import type { TypeaheadCreateNew } from '@/components/ui/typeahead';
import { useCan } from '@/lib/permissions';

import { QuickCreateWindow } from './quick-create-window';
import { QUICK_CREATE_ROSTER, type PickerEntity } from './roster';

/**
 * GEN-51 FOR ONE PICKER — the roster, the permission, the window and the trip
 * back, bound together so no call site has to remember any of them.
 *
 * A picker asks for two things and renders them:
 *
 *   const quick = useQuickCreate('supplier', (id) => setPartnerId(id));
 *   <Typeahead … createNew={quick.createNew} />
 *   {quick.dialog}
 *
 * Everything else — which side of D26 this entity is on, whether the role may
 * create it, what the window is called, where the jump goes — is read from
 * `QUICK_CREATE_ROSTER`, so a picker cannot get any of it wrong locally.
 *
 * ── `createNew: undefined` IS GEN-51'S ROLE CLAUSE ────────────────────────
 *
 * Spec line 3767 ends *"…if the user's role allows creating that record."*
 * When `useCan` says no, this returns no row at all, and `<Typeahead>` then
 * has nothing to draw, nothing to walk onto and nothing to announce. The
 * decision is taken HERE rather than in the component so that the component
 * stays ignorant of permissions and there is exactly one place the rule's
 * condition is evaluated. It is not a security boundary and does not pretend
 * to be one — BR-PERM-50: the server refuses the create either way.
 *
 * ── WHERE THE FOCUS GOES, AND WHY IT IS CAPTURED HERE ─────────────────────
 *
 * D26 asks that "focus returns sensibly". At the moment the create row is
 * chosen the picker's input still holds focus — the row `preventDefault`s its
 * own mousedown and the Enter path never moves it — so `document.activeElement`
 * read inside `onSelect` IS the control the reader came from. It is stashed
 * and restored when the window closes, on every exit: Save, Cancel, Escape and
 * the backdrop. Restoring on some exits and not others is worse than
 * restoring on none, because the reader learns a rule that then breaks.
 *
 * This is captured rather than passed as a ref from the caller so that the
 * thirteen partner pickers do not each have to wire one, and so a picker that
 * is not a `<Typeahead>` at all — the native-select sites for categories and
 * units — gets the same behaviour from the same code.
 */
export function useQuickCreate(
  entity: PickerEntity,
  /** The new record's id, once it exists. The picker selects it. */
  onCreated: (id: string) => void,
): { createNew: TypeaheadCreateNew | undefined; dialog: ReactNode } {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const entry = QUICK_CREATE_ROSTER[entity];
  const allowed = useCan(entry.permission);
  const [open, setOpen] = useState(false);
  const returnFocusTo = useRef<HTMLElement | null>(null);

  const close = useCallback(() => {
    setOpen(false);
    // `focus()` on a detached or vanished element is a no-op rather than a
    // throw, so no guard beyond the null check is needed.
    returnFocusTo.current?.focus();
    returnFocusTo.current = null;
  }, []);

  const createNew = useMemo((): TypeaheadCreateNew | undefined => {
    if (!allowed) return undefined;
    return {
      label: t('picker.createNew'),
      onSelect: () => {
        if (entry.mode === 'jump') {
          // D26's complex side. One navigation shape for every complex
          // entity, so "create-new jumps" means the same thing everywhere.
          // The unsaved-changes warning on the way out is the product being
          // honest about what leaving costs.
          void navigate(entry.route);
          return;
        }
        returnFocusTo.current =
          document.activeElement instanceof HTMLElement ? document.activeElement : null;
        setOpen(true);
      },
    };
  }, [allowed, entry, navigate, t]);

  const dialog =
    open && entry.mode === 'window' ? (
      <QuickCreateWindow
        entity={entity}
        titleKey={entry.titleKey}
        onClose={close}
        onCreated={(id) => {
          close();
          onCreated(id);
        }}
      />
    ) : null;

  return { createNew, dialog };
}
